import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { StatusBadge } from './StatusBadge';
import { Field } from './Field';
import { CopyButton } from './CopyButton';
import { apiGet, apiPost } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { STANDARDS_DISPLAY } from '../lib/standards';

/** Certification record as returned by `GET /v1/certs`. */
export interface CertData {
  id: number;
  schema: string;
  tier: number;
  type: 'test' | 'production';
  status: string;
  issuedAt: number;
  expiresAt: number | null;
  createdAt: number;
  renewed: number;
}

const TIER_INFO: Record<number, { name: string; maxBytes: number; sf: string; range: string }> = {
  0: { name: 'NANO', maxBytes: 51, sf: 'SF10 – SF12', range: 'up to 15mi' },
  1: { name: 'MICRO', maxBytes: 115, sf: 'SF9', range: 'up to 8mi' },
  2: { name: 'FULL', maxBytes: 222, sf: 'SF7 – SF8', range: 'up to 4mi' },
};

function fmtDate(unix: number | null): string {
  if (!unix) return '—';
  return new Date(unix * 1000).toISOString().slice(0, 10);
}

interface Countdown { text: string; cls: string }

function countdown(expiresAt: number | null): Countdown {
  if (!expiresAt) return { text: 'no expiry', cls: 'text-muted' };
  const now = Math.floor(Date.now() / 1000);
  const diffDays = Math.floor((expiresAt - now) / 86400);
  if (diffDays < 0) return { text: `expired ${-diffDays} days ago`, cls: 'text-danger' };
  if (diffDays < 7) return { text: `expires in ${diffDays} days — renew soon`, cls: 'text-danger' };
  if (diffDays < 30) return { text: `expires in ${diffDays} days`, cls: 'text-warn' };
  return { text: `expires in ${diffDays} days`, cls: 'text-muted' };
}

type BadgeStatus = 'active' | 'expired' | 'test' | 'prod' | 'revoked' | 'pending';

function badgeFor(status: string): BadgeStatus {
  if (status === 'active' || status === 'expired' || status === 'revoked' || status === 'pending') return status;
  return 'pending';
}

/**
 * Expandable card showing a cert's tier, expiry, and a renew action.
 * @param onChange Called after a successful renew so the parent can refetch.
 */
export function CertCard({ cert, onChange }: { cert: CertData; onChange?: () => void }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const { user, isAdmin } = useAuth();

  const tier = TIER_INFO[cert.tier] ?? TIER_INFO[0]!;
  const cd = countdown(cert.expiresAt);
  const renewedDisplay = cert.type === 'test'
    ? `${cert.renewed} of 1`
    : 'renewable';

  async function renew(e: React.MouseEvent) {
    e.stopPropagation();
    setErr(null);
    setBusy(true);
    try {
      await apiPost(`/v1/certs/${cert.id}/renew`, {});
      onChange?.();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'renewal failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="cert-card">
      <div className="cert-card-header" role="button" tabIndex={0} aria-expanded={open} aria-controls={`cert-details-${cert.id}`} onClick={() => setOpen((o) => !o)} onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); setOpen((value) => !value); } }}>
        <div style={{ flex: 1 }}>
          <div style={{ marginBottom: 4 }}>
            <strong>{(STANDARDS_DISPLAY as Record<string, string>)[cert.schema] ?? cert.schema}</strong> · Tier {cert.tier} {tier.name} ·{' '}
            <StatusBadge status={cert.type === 'production' ? 'prod' : 'test'} />{' '}
            <StatusBadge status={badgeFor(cert.status)} />
          </div>
          <div className="text-muted" style={{ fontSize: 'var(--font-size-sm)' }}>
            issued {fmtDate(cert.issuedAt)} · expires {fmtDate(cert.expiresAt)} · <span className={cd.cls}>{cd.text}</span>
          </div>
        </div>
        <div className="text-muted">[{open ? 'collapse ↑' : 'expand ↓'}]</div>
      </div>
      <div id={`cert-details-${cert.id}`} className={`cert-card-body ${open ? 'open' : ''}`} hidden={!open} inert={!open}>
        <div className="cert-card-body-inner">
          <div className="grid-2">
            <Field label="type" value={cert.type} />
            <Field label="duration" value={cert.type === 'test' ? '30 days' : '60 days'} />
            <Field label="issued" value={fmtDate(cert.issuedAt)} />
            <Field label="expires" value={fmtDate(cert.expiresAt)} />
            <Field label="tier" value={`${cert.tier} — ${tier.name}`} />
            <Field label="max bytes" value={tier.maxBytes} />
            <Field label="renewed" value={renewedDisplay} />
            <Field label="status" value={cert.status} />
          </div>
          {err && <p className="text-danger" style={{ marginTop: 8 }}>✗ {err}</p>}

          <hr className="divider" />
          <div className="text-muted" style={{ marginBottom: 8 }}>── api keys for {cert.schema} ─────────────────</div>
          <KeysForSchema schema={cert.schema} />

          <hr className="divider" />
          <div className="text-muted" style={{ marginBottom: 8 }}>── certification language ────────────────────</div>
          {cert.type === 'production' ? (
            <ProductionCertLanguage schema={cert.schema} tier={cert.tier} tierName={tier.name} orgId={user?.orgId ?? 0} />
          ) : (
            <p className="text-muted" style={{ fontSize: 'var(--font-size-sm)' }}>
              test certifications do not include display rights.<br />
              upgrade to production for certified badge language.
            </p>
          )}

          <hr className="divider" />
          <div className="text-muted" style={{ marginBottom: 8 }}>── actions ────────────────────────────────────</div>
          <button type="button" className="btn" disabled={busy || !isAdmin || (cert.type === 'test' && cert.renewed >= 1)} onClick={renew}>
            {busy ? 'renewing...' : 'renew'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ProductionCertLanguage({ schema, tier, tierName, orgId }: { schema: string; tier: number; tierName: string; orgId: number }) {
  const display = (STANDARDS_DISPLAY as Record<string, string>)[schema] ?? `${schema}\u2122`;
  const verifyUrl = `https://app.astdz.com/verify/${orgId}`;
  const mark = `${display} Certified by AstdZ\u2122 · Beta`;
  const badge = `${display} Certified | Tier ${tier} ${tierName} | Verified by AstdZ\u2122 · Beta`;
  return (
    <div style={{ fontSize: 'var(--font-size-sm)' }}>
      <p className="text-muted" style={{ marginBottom: 4 }}>you are authorized to display:</p>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
        <pre className="hex-display" style={{ flex: 1, margin: 0 }}>{`"${mark}"`}</pre>
        <CopyButton text={mark} label="copy" />
      </div>
      <p className="text-muted" style={{ marginBottom: 4 }}>this mark must link to your public verification page:</p>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
        <pre className="hex-display" style={{ flex: 1, margin: 0 }}>{verifyUrl}</pre>
        <CopyButton text={verifyUrl} label="copy" />
      </div>
      <p className="text-muted" style={{ marginBottom: 4 }}>
        use of this mark is subject to the <a href="/terms" target="_blank" rel="noreferrer">Terms of Service</a>.
        the mark may only be displayed while your certification is active.
      </p>
      <p className="text-muted" style={{ marginTop: 8, marginBottom: 4 }}>sample badge text:</p>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <pre className="hex-display" style={{ flex: 1, margin: 0 }}>{`"${badge}"`}</pre>
        <CopyButton text={badge} label="copy" />
      </div>
    </div>
  );
}

interface KeyScope { schema_code: string; tier_access: number }
interface KeyRow {
  id: number;
  label: string | null;
  keyPrefix: string | null;
  status: 'active' | 'expiring' | 'revoked' | 'expired';
  isLegacyWildcard: boolean;
  lastUsedAt: number | null;
  scopes: KeyScope[];
}

const TIER_NAMES = ['NANO', 'MICRO', 'FULL'];

function fmtUsed(t: number | null): string {
  if (!t) return 'never used';
  const ms = t > 1e12 ? t : t * 1000;
  const days = Math.floor((Date.now() - ms) / 86_400_000);
  if (days === 0) return 'used today';
  if (days === 1) return 'used yesterday';
  if (days < 30) return `used ${days}d ago`;
  return `used ${new Date(ms).toISOString().slice(0, 10)}`;
}

/**
 * Lists the org's API keys that have scope for `schema` (or are legacy
 * wildcard). Shows label, prefix, scope tier, status, and last-used so the
 * user can identify which key in their wallet is the right one for this cert.
 * Plaintext is never shown — to get a value on a fresh device, rotate or mint
 * via the Keys page.
 */
function KeysForSchema({ schema }: { schema: string }) {
  const [keys, setKeys] = useState<KeyRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiGet<{ keys: KeyRow[] }>('/v1/keys')
      .then((res) => {
        const filtered = res.keys.filter(
          (k) =>
            (k.status === 'active' || k.status === 'expiring') &&
            (k.isLegacyWildcard || k.scopes.some((s) => s.schema_code === schema))
        );
        setKeys(filtered);
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'failed to load keys'));
  }, [schema]);

  if (error) {
    return <p className="text-danger" style={{ fontSize: 'var(--font-size-sm)' }}>✗ {error}</p>;
  }
  if (keys === null) {
    return <p className="text-muted" style={{ fontSize: 'var(--font-size-sm)' }}>loading keys…</p>;
  }
  if (keys.length === 0) {
    return (
      <div>
        <p className="text-muted" style={{ fontSize: 'var(--font-size-sm)', marginBottom: 8 }}>
          no api keys with access to <code>{schema}</code> yet.
        </p>
        <Link to="/dashboard/keys" className="btn btn-primary">→ mint a key for {schema}</Link>
      </div>
    );
  }

  return (
    <div>
      <p className="text-muted" style={{ fontSize: 'var(--font-size-sm)', marginBottom: 8 }}>
        {keys.length} key{keys.length === 1 ? '' : 's'} can authenticate against {schema}.
        plaintext is never re-shown — to get a value on a new device, rotate the key on the keys page.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {keys.map((k) => {
          const scope = k.scopes.find((s) => s.schema_code === schema);
          const tierLabel = k.isLegacyWildcard
            ? 'legacy (all tiers)'
            : scope
              ? `tier ${scope.tier_access} ${TIER_NAMES[scope.tier_access] ?? ''}`
              : '';
          return (
            <div
              key={k.id}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 8,
                padding: '6px 10px',
                border: '1px solid var(--color-border)',
                borderRadius: 4,
                fontSize: 'var(--font-size-sm)',
                flexWrap: 'wrap',
              }}
            >
              <div>
                <strong>{k.label ?? `key #${k.id}`}</strong>
                <span className="text-muted"> · prefix </span>
                <code>{k.keyPrefix ?? '—'}…</code>
                <span className="text-muted"> · {tierLabel}</span>
              </div>
              <div className="text-muted">{fmtUsed(k.lastUsedAt)}</div>
            </div>
          );
        })}
      </div>
      <div style={{ marginTop: 10 }}>
        <Link to="/dashboard/keys" className="btn">→ manage keys</Link>
      </div>
    </div>
  );
}
